import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SelectAndReveal from 'typescript/components/select_and_reveal/select_and_reveal';
import useSelectAndReveal from 'typescript/components/select_and_reveal/use_select_and_reveal';
import PageTitle from 'typescript/apps/checkout_v3/components/page_title';
import {
  DeliveryOption,
  SelectAndRevealOptionType,
} from 'typescript/components/select_and_reveal/types';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useListPaymentOptions } from 'typescript/hooks/use_payment_options_client';
import { usePrefetchOffers } from 'typescript/hooks/use_offers_client';
import { onCheckoutPageViewCombined } from 'typescript/utilities/tracking';
import { COMPONENT_NAMES } from 'typescript/constants/tracking';
import { orderHasInstallments } from 'typescript/utilities/payment_options';

const Delivery = () => {
  const { t } = useTranslation();
  const {
    orderNumber,
    deliveryOption,
    setDeliveryOption,
    trackingData,
    deliveryOptions = {
      [DeliveryOption.SHIP_TO_ME]: true,
    },
  } = useCheckoutFlow();
  const { prefetchOffers } = usePrefetchOffers({ orderNumber });
  const { paymentOptionsData, isPending: paymentOptionsIsPending } =
    useListPaymentOptions({
      orderNumber,
    });

  const hasInstallments =
    !paymentOptionsIsPending &&
    orderHasInstallments(paymentOptionsData?.payment_options ?? []);

  // deliveryOptions looks like { ship_to_me: true, bopis: false }
  const selectAndRevealOptions = useMemo(
    () =>
      Object.entries(deliveryOptions).reduce((options, [type, isAvailable]) => {
        if (isAvailable) {
          options.push({ option: DeliveryOption[type.toUpperCase()] });
        }
        return options;
      }, []),
    [deliveryOptions],
  );

  const { selectOptions } = useSelectAndReveal({
    optionType: SelectAndRevealOptionType.DELIVERY,
    options: selectAndRevealOptions,
  });

  // Endpoint does not depend on address, so safe to pre-fetch.
  useEffect(() => {
    if (hasInstallments) {
      prefetchOffers();
    }
  }, [hasInstallments]);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.DELIVERY,
      additionalParameters: trackingData,
    });
  }, []);

  return (
    <div className="shop-grid-container">
      <PageTitle
        className="shop-grid-item-two-third"
        title={t('checkout.delivery.title.pageHeader')}
        subtitle={t('checkout.delivery.subtitle.pageHeader')}
      />
      <SelectAndReveal
        className="shop-grid-item-full"
        selectOptions={selectOptions}
        optionSelected={deliveryOption}
        setOptionSelected={setDeliveryOption}
      />
    </div>
  );
};

export default Delivery;
